import React from "react";
import PropTypes from "prop-types";
import {
    Skeleton,
    Box,
    Stack,
    Pagination
} from "@mui/material";
import { formatDateTime } from '../../../utils/StringUtil';
import {
    SNACKBAR_ERROR,
    SNACKBAR_SUCCESS,
    SNACKBAR_WARNING,
    RECORD_DISPLAY_LIMIT
} from "../../../utils/constants/constants";
/**
 * DealBookingHistory Component
 * 
 * Displays a paginated table of deal booking history.
 * Includes loading states and empty state handling.
 * 
 * @param {Object} props - Component props
 * @param {string|null} props.oppRid - ID of the selected scheme
 * @param {Array} props.dealBookingList - List of deal bookings to display
 * @param {boolean} props.isLoading - Whether data is being loaded
 * @param {number} props.page - Current page number
 * @param {number} props.totalRecords - Total number of records
 * @param {Function} props.onPageChange - Callback when page changes
 */
const DealBookingHistory = ({
    oppRid,
    dealBookingList,
    isLoading,
    page,
    totalRecords,
    onPageChange
}) => {
    if (oppRid === null) {
        return null;
    }
    return (
        <Stack sx={{ marginBottom: "2rem" }} direction="column" spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <p className="section-label p-0 m-0">Deal Booking History</p>
                    <Pagination
                        color="secondary"
                        size="small"
                        siblingCount={0}
                        onChange={onPageChange}
                        count={Math.max(1, totalRecords > 0 ? Math.ceil(totalRecords / RECORD_DISPLAY_LIMIT) : 1)}
                        page={page}
                    />
              
            </Box>

            {renderContent(isLoading, dealBookingList)}
        </Stack>
    );
};

/**
 * Renders the appropriate content based on loading state and data
 * 
 * @param {boolean} isLoading - Whether data is being loaded
 * @param {Array} dealBookingList - List of deal bookings to display
 * @returns {JSX.Element} The rendered content
 */
const renderContent = (isLoading, dealBookingList) => {
    if (isLoading) {
        return (
            <div>
                <p className="fs-med-regular p-0 m-0 mb-2">Loading...</p>
                {[...Array(5)].map((_, index) => (
                    <Skeleton
                        animation="wave"
                        key={index}
                        style={{ marginBottom: 6 }}
                        variant="rectangle"
                        height={40}
                    />
                ))}
            </div>
        );
    }

    if (dealBookingList.length === 0) {
        return <p className="fs-med-regular p-0 m-0">No Data</p>;
    }

    return (
        <table className="table table-striped table-borderless w-100">
            <thead>
                <tr className="fs-normal-semibold text-center">
                    <th scope="col">
                        <p className="mb-2">Scheme Name</p>
                    </th>
                    <th scope="col">
                        <p className="mb-2">Company Legal Name</p>
                    </th>
                    <th scope="col">
                        <p className="mb-2">Posting Date</p>
                    </th>
                    <th scope="col">
                        <p className="mb-2">Purchase Quantity (Units)</p>
                    </th>
                    <th scope="col">
                        <p className="mb-2">Purchase Rate per Unit</p>
                    </th>
                    <th scope="col">
                        <p className="mb-2">Unit Type</p>
                    </th>
                </tr>
            </thead>
            <tbody>
                {dealBookingList.map((item, index) => (
                    <tr
                        className="fs-normal-medium text-center"
                        key={item.dbRid || index}
                    >
                        <td className="pt-3">{item.oppTitle || "---"}</td>
                        <td className="pt-3">{item.legalNameOfEntity || "---"}</td>
                        <td className="pt-3">{item.postingDate ? formatDateTime(item.postingDate, false) : "---"}</td>
                        <td className="pt-3">{item.purchaseQuantity !== null && item.purchaseQuantity !== undefined ? parseFloat(item.purchaseQuantity).toLocaleString() : "---"}</td>
                        <td className="pt-3">{item.purchaseRate !== null && item.purchaseRate !== undefined ? `₹${parseFloat(item.purchaseRate).toLocaleString()}` : "---"}</td>
                        <td className="pt-3">{item.unitType || "---"}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

DealBookingHistory.propTypes = {
    oppRid: PropTypes.string,
    dealBookingList: PropTypes.array.isRequired,
    isLoading: PropTypes.bool.isRequired,
    page: PropTypes.number.isRequired,
    totalRecords: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired
};

export default DealBookingHistory;